import { useState, useEffect, useRef } from "react";
import Consultation from "../pages/Consultation"; // ← adjust path to match your project
import HowItWorksHeroDesktop from "../assets/images/How-it-work/How-it-work-hero.jpeg";
import HowItWorksHeroMobile from "../assets/images/How-it-work/How-it-work-hero-mobile.png";
import Work1 from "../assets/images/How-it-work/Work-1.png";
import Work2 from "../assets/images/How-it-work/Work-2.png";
import Work3 from "../assets/images/How-it-work/Work-3.png";
import Work4 from "../assets/images/How-it-work/Work-4.png";
import Work5 from "../assets/images/How-it-work/Work-5.png";
import SEO from "../components/SEO";

const steps = [
  {
    number: "01",
    title: "Initial Consultation",
    subtitle: "Understanding Your Vision",
    description:
      "We begin with a comprehensive site visit and consultation to assess your facility's current state, understand your clinical workflows, patient volume, and regulatory requirements. Our healthcare architects listen first — designing second.",
    milestones: [
      {
        icon: "🏥",
        title: "Site Assessment",
        desc: "In-depth evaluation of existing infrastructure, spatial constraints, and utility networks.",
      },
      {
        icon: "📋",
        title: "Requirements Gathering",
        desc: "Capture your operational needs: OT count, ICU beds, patient flow, NABH compliance goals.",
      },
    ],
    image: Work1,
    tag: "Week 1–2",
    navIcon: "🩺",
  },
  {
    number: "02",
    title: "Design & Planning",
    subtitle: "Precision-Engineered Layouts",
    description:
      "Our multidisciplinary team crafts zone-specific designs — sterile zones, ICUs, OTs, pharma areas — aligned with NABH, NABH Excellence, and HCI standards. Every square foot is optimised for clinical efficiency and patient safety.",
    milestones: [
      {
        icon: "📐",
        title: "Zoning & Layout",
        desc: "Infection-control zoning, traffic segregation, and emergency access planning.",
      },
      {
        icon: "⚡",
        title: "MEP Engineering",
        desc: "Medical gas pipelines, nurse call systems, HVAC pressurisation, and power redundancy design.",
      },
    ],
    image: Work2,
    tag: "Week 3–6",
    navIcon: "📐",
  },
  {
    number: "03",
    title: "Approvals & Compliance",
    subtitle: "Regulatory Fast-Track",
    description:
      "We handle end-to-end statutory approvals — municipal NOCs, fire safety certificates, PCB clearances, and AERB licences for radiology. Our dedicated compliance team ensures zero-delay project kickoff.",
    milestones: [
      {
        icon: "📜",
        title: "NABH Pre-Assessment",
        desc: "Gap analysis and documentation support for NABH Entry Level / Full Accreditation.",
      },
      {
        icon: "🔒",
        title: "Fire & Safety NOC",
        desc: "Fire suppression, emergency egress, and sprinkler compliance coordination.",
      },
    ],
    image: Work3,
    tag: "Week 5–8",
    navIcon: "📜",
  },
  {
    number: "04",
    title: "Execution & Build",
    subtitle: "Hospital-Grade Construction",
    description:
      "Our on-site project managers oversee every phase — civil, MEP, modular OT installation, clean-room flooring, and medical gas commissioning — with dedicated infection-control protocols during active hospital operations.",
    milestones: [
      {
        icon: "🔧",
        title: "Phased Construction",
        desc: "Zero-disruption build methodology keeping active wards fully operational.",
      },
      {
        icon: "🏗️",
        title: "Modular OT & ICU",
        desc: "Class 100 / Class 10,000 laminar airflow OT installation with full validation.",
      },
    ],
    image: Work4,
    tag: "Month 2–8",
    navIcon: "🏗️",
  },
  {
    number: "05",
    title: "Handover & Support",
    subtitle: "Ready to Heal",
    description:
      "We conduct rigorous commissioning, staff orientation, and a final NABH-readiness walkthrough before handing over your facility. Post-handover, our AMC team ensures systems stay operational 24×7.",
    milestones: [
      {
        icon: "✅",
        title: "Commissioning & Testing",
        desc: "Full validation of OT air quality, medical gases, nurse call, and BMS systems.",
      },
      {
        icon: "🤝",
        title: "Post-Handover AMC",
        desc: "Annual maintenance contracts covering all critical infrastructure systems.",
      },
    ],
    image: Work5,
    tag: "Final Phase",
    navIcon: "✅",
  },
];

const team = [
  {
    title: "Healthcare Architect",
    desc: "Designs infection-control zoning, patient flow pathways, and clinical department layouts tailored to your specialty.",
    icon: "🏛️",
  },
  {
    title: "MEP Engineer",
    desc: "Oversees medical gas pipelines, HVAC pressurisation, electrical redundancy, and nurse call integration.",
    icon: "⚙️",
  },
  {
    title: "Compliance Manager",
    desc: "Drives NABH documentation, statutory approvals, and regulatory liaison — keeping your timeline on track.",
    icon: "📋",
  },
  {
    title: "Site Project Manager",
    desc: "Commands on-site execution with zero-disruption protocols, daily progress tracking, and quality control.",
    icon: "👷",
  },
];

export default function HowItWorks() {
  const [activeStep, setActiveStep] = useState(0);
  const [isMobile, setIsMobile] = useState(false);
  const [showConsultation, setShowConsultation] = useState(false);
  const stepRefs = useRef([]);

  const openConsultation = () => setShowConsultation(true);
  const closeConsultation = () => setShowConsultation(false);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth <= 768);
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const idx = parseInt(entry.target.dataset.index);
            setActiveStep(idx);
          }
        });
      },
      { threshold: 0.4 }
    );
    stepRefs.current.forEach((ref) => ref && observer.observe(ref));
    return () => observer.disconnect();
  }, []);

  return (
     <>
    <SEO/>
    <div style={styles.root}>

      {/* ── HERO ── */}
      <section style={isMobile ? styles.heroMobile : styles.hero}>
        <div style={styles.heroGrid} />
        <div style={isMobile ? styles.heroVisualMobile : styles.heroVisual}>
          <div style={styles.heroImageWrap} onClick={openConsultation}>
            <img
              src={isMobile ? HowItWorksHeroMobile : HowItWorksHeroDesktop}
              alt="Hospital infrastructure planning"
              style={isMobile ? styles.heroImgMobile : styles.heroImg}
            />
            <div style={styles.heroImageOverlay} />
          </div>
        </div>
      </section>

      {/* ── LIVSPACE-STYLE 5-STEP STRIP ── */}
      <section style={isMobile ? styles.stripSectionMobile : styles.stripSection}>
        <div style={styles.stripTitleRow}>
          <div style={styles.stripTitleBar} />
          <h2 style={isMobile ? styles.stripHeadingMobile : styles.stripHeading}>
            Your hospital, built in 5 steps
          </h2>
        </div>
        <p style={styles.stripSub}>
          Looking to build your hospital? Here's how you can get started.
        </p>
        <div style={isMobile ? styles.stripRowMobile : styles.stripRow}>
          {steps.map((s, i) => (
            <div
              key={i}
              style={isMobile ? styles.stripItemWrapMobile : styles.stripItemWrap}
            >
              <div
                style={styles.stripCircleCol}
                onClick={() =>
                  stepRefs.current[i]?.scrollIntoView({ behavior: "smooth", block: "center" })
                }
              >
                <div style={isMobile ? styles.stripCircleMobile : styles.stripCircle}>
                  <span style={styles.stripIcon}>{s.navIcon}</span>
                  <div style={styles.stripBadge}>{i + 1}</div>
                </div>
                <span style={styles.stripLabel}>{s.title}</span>
              </div>
              {i < steps.length - 1 && !isMobile && (
                <div style={styles.stripArrow}>
                  <svg width="52" height="14" viewBox="0 0 52 14" fill="none">
                    <line x1="2" y1="7" x2="40" y2="7" stroke="#A7ACBD" strokeWidth="1.5" strokeDasharray="5 3" />
                    <polyline points="34,3 42,7 34,11" fill="none" stroke="#A7ACBD" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </div>
              )}
            </div>
          ))}
        </div>
        <button style={styles.stripCta} onClick={openConsultation}>START YOUR PROJECT NOW</button>
      </section>

      {/* ── DETAILED STEPS ── */}
      <section style={isMobile ? styles.stepsSectionMobile : styles.stepsSection}>
        {steps.map((step, i) => (
          <div
            key={i}
            data-index={i}
            ref={(el) => (stepRefs.current[i] = el)}
            style={{
              ...(isMobile ? styles.stepBlockMobile : styles.stepBlock),
              ...(!isMobile && i % 2 === 1 ? styles.stepBlockReverse : {}),
            }}
          >
            <div style={styles.stepImageSide}>
              <div style={styles.stepImageWrap}>
                <img
                  src={step.image}
                  alt={step.title}
                  style={isMobile ? styles.stepImageMobile : styles.stepImage}
                />
                <div style={styles.stepImageOverlay} />
                <div style={styles.stepTag}>{step.tag}</div>
                <div style={styles.stepNumBig}>{step.number}</div>
              </div>
            </div>

            <div style={styles.stepContentSide}>
              <div style={styles.stepSubtitle}>{step.subtitle}</div>
              <h2 style={isMobile ? styles.stepTitleMobile : styles.stepTitle}>
                {step.title}
              </h2>
              <p style={styles.stepDesc}>{step.description}</p>
              <div style={styles.milestonesGrid}>
                {step.milestones.map((m, j) => (
                  <div key={j} style={styles.milestoneCard}>
                    <span style={styles.milestoneIcon}>{m.icon}</span>
                    <div>
                      <div style={styles.milestoneTitle}>{m.title}</div>
                      <div style={styles.milestoneDesc}>{m.desc}</div>
                    </div>
                  </div>
                ))}
              </div>
              {i === 0 && (
                <button style={styles.stepCta} onClick={openConsultation}>
                  Free Consultation →
                </button>
              )}
            </div>

            {i < steps.length - 1 && (
              <div style={styles.connector}>
                <div style={styles.connectorLine} />
                <div style={styles.connectorDot} />
              </div>
            )}
          </div>
        ))}
      </section>

      {/* ── TEAM ── */}
      <section style={isMobile ? styles.teamSectionMobile : styles.teamSection}>
        <div style={styles.teamHeader}>
          <div style={styles.sectionLabel}>Your Dedicated Team</div>
          <h2 style={isMobile ? styles.sectionTitleMobile : styles.sectionTitle}>
            The Experts Behind<br />Your Hospital
          </h2>
          <p style={styles.sectionSub}>
            Four specialists work in concert across every project phase — ensuring clinical precision at every turn.
          </p>
        </div>
        <div style={styles.teamGrid}>
          {team.map((t, i) => (
            <div key={i} style={styles.teamCard}>
              <div style={styles.teamIcon}>{t.icon}</div>
              <h3 style={styles.teamTitle}>{t.title}</h3>
              <p style={styles.teamDesc}>{t.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── CTA BANNER ── */}
      <section style={isMobile ? styles.ctaBannerMobile : styles.ctaBanner}>
        <div style={styles.ctaContent}>
          <div style={styles.ctaLabel}>Ready to Begin?</div>
          <h2 style={isMobile ? styles.ctaTitleMobile : styles.ctaTitle}>
            Your Hospital Journey<br />Starts Here
          </h2>
          <p style={styles.ctaSub}>
            Tell us about your project and our team will reach out within 24 hours with a customised plan.
          </p>
          <div style={styles.ctaButtons}>
            <button style={styles.ctaPrimary} onClick={openConsultation}>Free Consultation</button>
            <button style={styles.ctaSecondary}>Download Brochure</button>
          </div>
        </div>
      </section>

      {/* ── CONSULTATION MODAL ── */}
      {showConsultation && (
        <div style={styles.modalOverlay} onClick={closeConsultation}>
          <div style={styles.modalBox} onClick={(e) => e.stopPropagation()}>
            <button style={styles.modalClose} onClick={closeConsultation}>×</button>
            <Consultation />
          </div>
        </div>
      )}

    </div>
    </>
  );
}

/* ── PALETTE ──────────────────────────────────────────────
   black   : #343F86
   charcoal: #262E63
   grey    : #5B6072
   ash     : #8A8F9C
   silver  : #A7ACBD
   mist    : #CDD1E0
   offwhite: #ECEEF6
   white   : #FFFFFF
───────────────────────────────────────────────────────── */

const styles = {
  root: {
    fontFamily: "'Inter', 'Montserrat', sans-serif",
    background: "#ECEEF6",
    color: "#343F86",
    minHeight: "100vh",
    overflowX: "hidden",
  },

  /* HERO — DESKTOP */
  hero: {
    minHeight: "100vh",
    background: "#343F86",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 0,
    position: "relative",
    overflow: "hidden",
  },
  heroVisual: {
    zIndex: 1,
    width: "100%",
  },
  heroImg: {
    width: "100%",
    height: "auto",
    objectFit: "contain",
    display: "block",
    filter: "grayscale(30%)",
  },

  /* HERO — MOBILE */
  heroMobile: {
    minHeight: "auto",
    background: "#343F86",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 0,
    position: "relative",
    overflow: "hidden",
  },
  heroVisualMobile: {
    zIndex: 1,
    width: "100%",
  },
  heroImgMobile: {
    width: "100%",
    height: "auto",
    objectFit: "contain",
    display: "block",
    filter: "grayscale(30%)",
  },

  heroGrid: {
    position: "absolute", inset: 0,
    backgroundImage:
      "linear-gradient(rgba(255,255,255,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.04) 1px, transparent 1px)",
    backgroundSize: "60px 60px",
    pointerEvents: "none",
  },
  heroImageWrap: {
    position: "relative",
    width: "100%",
    cursor: "pointer",
  },
  heroImageOverlay: {
    position: "absolute",
    inset: 0,
    background: "linear-gradient(180deg, transparent 70%, rgba(0,0,0,0.55) 100%)",
  },

  /* 5-STEP STRIP — DESKTOP */
  stripSection: {
    background: "#FFFFFF",
    padding: "56px 60px 48px",
    textAlign: "center",
    borderBottom: "1px solid #CDD1E0",
  },
  stripHeading: {
    fontSize: 26, fontWeight: 700, color: "#343F86",
    margin: 0, letterSpacing: "-0.01em",
  },
  stripRow: {
    display: "flex", alignItems: "flex-start",
    justifyContent: "center", gap: 0,
    flexWrap: "wrap", marginBottom: 40,
  },
  stripItemWrap: { display: "flex", alignItems: "center", gap: 0 },
  stripCircle: {
    width: 84, height: 84, borderRadius: "50%",
    border: "1.5px solid #CDD1E0",
    background: "#ECEEF6",
    display: "flex", alignItems: "center",
    justifyContent: "center", position: "relative",
  },

  /* 5-STEP STRIP — MOBILE */
  stripSectionMobile: {
    background: "#FFFFFF",
    padding: "40px 20px 32px",
    textAlign: "center",
    borderBottom: "1px solid #CDD1E0",
  },
  stripHeadingMobile: {
    fontSize: 20, fontWeight: 700, color: "#343F86",
    margin: 0, letterSpacing: "-0.01em",
  },
  stripRowMobile: {
    display: "grid",
    gridTemplateColumns: "repeat(3, 1fr)",
    gap: "24px 8px",
    justifyItems: "center",
    marginBottom: 32,
  },
  stripItemWrapMobile: { display: "flex", alignItems: "center" },
  stripCircleMobile: {
    width: 64, height: 64, borderRadius: "50%",
    border: "1.5px solid #CDD1E0",
    background: "#ECEEF6",
    display: "flex", alignItems: "center",
    justifyContent: "center", position: "relative",
  },

  stripTitleRow: {
    display: "flex", alignItems: "center",
    justifyContent: "center", gap: 10, marginBottom: 12,
  },
  stripTitleBar: {
    width: 4, height: 28,
    background: "#343F86", borderRadius: 2, flexShrink: 0,
  },
  stripSub: {
    fontSize: 15, color: "#8A8F9C",
    margin: "0 0 40px", lineHeight: 1.6,
  },
  stripCircleCol: {
    display: "flex", flexDirection: "column",
    alignItems: "center", gap: 10,
    cursor: "pointer", width: 108,
  },
  stripIcon: { fontSize: 30 },
  stripBadge: {
    position: "absolute", top: -5, left: -5,
    width: 22, height: 22, borderRadius: "50%",
    background: "#343F86", color: "#FFFFFF",
    display: "flex", alignItems: "center",
    justifyContent: "center",
    fontSize: 11, fontWeight: 700,
  },
  stripLabel: {
    fontSize: 12, color: "#5B6072",
    textAlign: "center", lineHeight: 1.4, maxWidth: 90,
  },
  stripArrow: {
    display: "flex", alignItems: "center",
    paddingBottom: 32, margin: "0 2px",
  },
  stripCta: {
    background: "#343F86", color: "#FFFFFF",
    border: "none", borderRadius: 40,
    padding: "15px 48px", fontSize: 13,
    fontWeight: 700, cursor: "pointer",
    letterSpacing: "0.08em",
  },

  /* DETAILED STEPS — DESKTOP */
  stepsSection: {
    padding: "80px 60px",
    background: "#ECEEF6",
    display: "flex", flexDirection: "column", gap: 0,
  },
  stepBlock: {
    display: "flex", gap: 80, alignItems: "center",
    padding: "80px 0", borderBottom: "1px solid #CDD1E0",
    position: "relative",
  },
  stepBlockReverse: { flexDirection: "row-reverse" },
  stepImage: {
    width: "100%", height: 420, objectFit: "cover",
    display: "block", borderRadius: 12,
    filter: "grayscale(20%)",
  },
  stepTitle: {
    fontSize: 40, fontWeight: 700,
    color: "#343F86", margin: "0 0 20px",
    lineHeight: 1.1, letterSpacing: "-0.02em",
  },

  /* DETAILED STEPS — MOBILE */
  stepsSectionMobile: {
    padding: "40px 20px",
    background: "#ECEEF6",
    display: "flex", flexDirection: "column", gap: 0,
  },
  stepBlockMobile: {
    display: "flex", flexDirection: "column", gap: 28,
    padding: "40px 0", borderBottom: "1px solid #CDD1E0",
    position: "relative",
  },
  stepImageMobile: {
    width: "100%", height: 240, objectFit: "cover",
    display: "block", borderRadius: 12,
    filter: "grayscale(20%)",
  },
  stepTitleMobile: {
    fontSize: 26, fontWeight: 700,
    color: "#343F86", margin: "0 0 14px",
    lineHeight: 1.15, letterSpacing: "-0.02em",
  },

  stepImageSide: { flex: 1, flexShrink: 0 },
  stepImageWrap: { position: "relative", borderRadius: 12, overflow: "hidden" },
  stepImageOverlay: {
    position: "absolute", inset: 0,
  },
  stepTag: {
    position: "absolute", top: 16, left: 16,
    background: "#343F86", color: "#FFFFFF",
    padding: "6px 14px", borderRadius: 20,
    fontSize: 12, fontWeight: 600,
    letterSpacing: "0.05em",
  },
  stepNumBig: {
    position: "absolute", bottom: -10, right: -10,
    fontSize: 120, fontWeight: 800,
    color: "rgba(0,0,0,0.05)", lineHeight: 1,
    userSelect: "none",
  },
  stepContentSide: { flex: 1 },
  stepSubtitle: {
    fontSize: 11, letterSpacing: "0.18em",
    textTransform: "uppercase", color: "#2F8A98",
    fontWeight: 600, marginBottom: 12,
  },
  stepDesc: {
    fontSize: 16, color: "#5B6072", lineHeight: 1.8,
    fontWeight: 300, marginBottom: 36,
  },
  milestonesGrid: { display: "flex", flexDirection: "column", gap: 14, marginBottom: 36 },
  milestoneCard: {
    display: "flex", gap: 16, alignItems: "flex-start",
    background: "#FFFFFF", borderRadius: 10,
    padding: "18px 20px",
    border: "1px solid #CDD1E0",
  },
  milestoneIcon: { fontSize: 26, flexShrink: 0, marginTop: 2 },
  milestoneTitle: {
    fontSize: 15, fontWeight: 600,
    color: "#343F86", marginBottom: 5,
  },
  milestoneDesc: { fontSize: 13, color: "#8A8F9C", lineHeight: 1.6 },
  stepCta: {
    background: "#343F86", color: "#FFFFFF",
    border: "none", borderRadius: 40,
    padding: "14px 36px", fontSize: 14,
    fontWeight: 700, cursor: "pointer",
    letterSpacing: "0.04em",
  },
  connector: {
    position: "absolute", bottom: -1, left: "50%",
    transform: "translateX(-50%)",
    display: "flex", flexDirection: "column", alignItems: "center",
  },
  connectorLine: { width: 2, height: 32, background: "#CDD1E0" },
  connectorDot: {
    width: 10, height: 10, borderRadius: "50%",
    background: "#A7ACBD", border: "2px solid #ECEEF6",
  },

  /* TEAM — DESKTOP / MOBILE */
  teamSection: {
    background: "#343F86",
    padding: "100px 60px",
  },
  teamSectionMobile: {
    background: "#343F86",
    padding: "56px 20px",
  },
  teamHeader: { textAlign: "center", marginBottom: 60 },
  sectionLabel: {
    fontSize: 11, letterSpacing: "0.18em",
    textTransform: "uppercase", color: "#2F8A98",
    fontWeight: 600, marginBottom: 16, display: "block",
  },
  sectionTitle: {
    fontSize: 40, fontWeight: 700,
    color: "#FFFFFF", margin: "0 0 20px",
    letterSpacing: "-0.02em", lineHeight: 1.15,
  },
  sectionTitleMobile: {
    fontSize: 26, fontWeight: 700,
    color: "#FFFFFF", margin: "0 0 16px",
    letterSpacing: "-0.02em", lineHeight: 1.2,
  },
  sectionSub: {
    fontSize: 16, color: "rgba(255,255,255,0.45)",
    fontWeight: 300, maxWidth: 500,
    margin: "0 auto", lineHeight: 1.75,
  },
  teamGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
    gap: 20,
  },
  teamCard: {
    background: "rgba(255,255,255,0.05)",
    border: "1px solid rgba(255,255,255,0.1)",
    borderRadius: 12, padding: "32px 24px",
  },
  teamIcon: { fontSize: 32, marginBottom: 18, display: "block" },
  teamTitle: {
    fontSize: 20, fontWeight: 600,
    color: "#FFFFFF", margin: "0 0 10px",
  },
  teamDesc: {
    fontSize: 13, color: "rgba(255,255,255,0.45)",
    lineHeight: 1.7, margin: 0,
  },

  /* CTA BANNER — DESKTOP / MOBILE */
  ctaBanner: {
    background: "#262E63",
    padding: "100px 60px",
    textAlign: "center",
    position: "relative",
    overflow: "hidden",
  },
  ctaBannerMobile: {
    background: "#262E63",
    padding: "56px 20px",
    textAlign: "center",
    position: "relative",
    overflow: "hidden",
  },
  ctaContent: { position: "relative", zIndex: 1 },
  ctaLabel: {
    fontSize: 11, letterSpacing: "0.18em",
    textTransform: "uppercase", color: "#7FD0DA",
    fontWeight: 600, marginBottom: 16, display: "block",
  },
  ctaTitle: {
    fontSize: 40, fontWeight: 700, color: "#FFFFFF",
    margin: "0 0 24px",
    letterSpacing: "-0.02em", lineHeight: 1.1,
  },
  ctaTitleMobile: {
    fontSize: 26, fontWeight: 700, color: "#FFFFFF",
    margin: "0 0 18px",
    letterSpacing: "-0.02em", lineHeight: 1.2,
  },
  ctaSub: {
    fontSize: 16, color: "rgba(255,255,255,0.45)",
    fontWeight: 300, maxWidth: 460,
    margin: "0 auto 48px", lineHeight: 1.75,
  },
  ctaButtons: { display: "flex", gap: 16, justifyContent: "center", flexWrap: "wrap" },
  ctaPrimary: {
    background: "#FFFFFF", color: "#2F8A98",
    border: "none", borderRadius: 40,
    padding: "16px 44px", fontSize: 14,
    fontWeight: 700, cursor: "pointer",
    letterSpacing: "0.06em", textTransform: "uppercase",
  },
  ctaSecondary: {
    background: "transparent", color: "#FFFFFF",
    border: "1px solid rgba(255,255,255,0.25)",
    borderRadius: 40, padding: "16px 44px", fontSize: 14,
    fontWeight: 500, cursor: "pointer",
  },

  /* CONSULTATION MODAL */
  modalOverlay: {
    position: "fixed", inset: 0,
    background: "rgba(0,0,0,0.6)",
    backdropFilter: "blur(4px)",
    display: "flex", alignItems: "center", justifyContent: "center",
    zIndex: 1000,
    padding: "20px",
  },
  modalBox: {
    position: "relative",
    background: "#343F86",
    borderRadius: 12,
    maxWidth: 560,
    width: "100%",
    maxHeight: "90vh",
    overflowY: "auto",
  },
  modalClose: {
    position: "absolute", top: 12, right: 16,
    background: "transparent", border: "none",
    color: "#FFFFFF", fontSize: 28,
    cursor: "pointer", lineHeight: 1, zIndex: 10,
  },
};